import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import { format } from 'date-fns';
import { Referral } from '~/types/transition';
import { useIntakeQueue } from '~/providers/IntakeQueueProvider';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';
import { useCapacitySnapshot } from '~/features/capacity-management/hooks/useCapacitySnapshot';
import { getDefaultDateRange } from '~/features/capacity-management/utils/dateHelpers';
import {
  getProviderRecommendations,
  filterViableRecommendations,
} from '~/features/capacity-management/engine/providerRecommendationEngine';
import {
  addTerritoryToProviders,
  getMockPanelSizes,
} from '~/features/capacity-management/mocks/mockIntakePatients';
import ProviderRecommendationRow from '~/features/capacity-management/components/ProviderRecommendationRow';

interface Props {
  referral: Referral;
}

const LABEL_SX = {
  textTransform: 'uppercase' as const,
  fontWeight: 600,
  letterSpacing: 0.5,
  fontSize: '0.68rem',
  color: 'text.disabled',
  display: 'block',
  mb: 0.5,
};

const TARGET_UTILIZATION = 75;

export const ReferralOperateStatusCard: React.FC<Props> = ({ referral }) => {
  const navigate = useNavigate();
  const { getEntryByReferralId, markAssigned } = useIntakeQueue();
  const { currentDemoUser } = useTransitionContext();

  const entry = getEntryByReferralId(referral.id);
  const hasOperateAccess = currentDemoUser.access.includes('operate');

  const [providerType, setProviderType] = useState<'RN' | 'PT' | 'OT'>('RN');

  // Capacity snapshot for recommendation engine
  const dateRange = useMemo(() => getDefaultDateRange(), []);
  const { snapshot } = useCapacitySnapshot({
    startDate: dateRange.start,
    endDate: dateRange.end,
    useMockData: true,
  });

  // Territory and panel metadata
  const territoryMap = useMemo(() => addTerritoryToProviders(), []);
  const panelSizes = useMemo(() => getMockPanelSizes(), []);

  const providerMetadata = useMemo(() => {
    if (!snapshot) return [];
    const unique = new Map<number, any>();
    snapshot.providers.forEach((day) => {
      if (!unique.has(day.providerId)) {
        unique.set(day.providerId, {
          providerId: day.providerId,
          providerName: day.providerName,
          role: day.role,
          territory: territoryMap[day.providerId] || 'Unknown',
          panelSize: panelSizes[day.providerId] || 0,
        });
      }
    });
    return Array.from(unique.values());
  }, [snapshot, territoryMap, panelSizes]);

  const filteredProviderMetadata = useMemo(
    () => providerMetadata.filter((p) => p.role === providerType),
    [providerMetadata, providerType]
  );

  const recommendations = useMemo(() => {
    if (!snapshot || !entry) return [];
    const patient = { ...entry.patient, requiredRole: providerType };
    return getProviderRecommendations(patient, snapshot.providers, filteredProviderMetadata, TARGET_UTILIZATION);
  }, [snapshot, entry, providerType, filteredProviderMetadata]);

  if (!entry) return null;

  const { patient } = entry;
  const isAssigned = patient.status === 'assigned';

  const handleAssign = (providerId: number, providerName: string) => {
    markAssigned(patient.id, providerName, patient.intakeVisitDate);
  };

  const handleAutoAssign = () => {
    const viable = filterViableRecommendations(recommendations, false);
    if (viable.length > 0) {
      markAssigned(patient.id, viable[0].providerName, patient.intakeVisitDate);
    }
  };

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ fontWeight: 700, letterSpacing: 1.2, color: isAssigned ? 'success.main' : 'info.main' }}
      >
        Operate
      </Typography>
      <Divider sx={{ mt: 1, mb: 2, borderBottomWidth: 2, borderColor: isAssigned ? 'success.main' : 'info.main' }} />

      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderColor: isAssigned ? 'success.light' : 'info.light',
          bgcolor: isAssigned ? 'success.50' : 'grey.50',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          {isAssigned
            ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22 }} />
            : <ScheduleSendIcon sx={{ color: 'info.main', fontSize: 22 }} />}
          <Typography variant="subtitle1" fontWeight={700}>
            {isAssigned ? 'Intake Assignment Complete' : 'Pending Intake Assignment'}
          </Typography>
          <Chip
            label={isAssigned ? 'Assigned' : 'Pending'}
            size="small"
            color={isAssigned ? 'success' : 'info'}
            sx={{ fontSize: '0.68rem', height: 20 }}
          />
        </Box>

        {/* Stage 2 — assigned */}
        {isAssigned && (
          <>
            <Grid container spacing={3} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <Typography sx={LABEL_SX}>Assigned Provider</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <AssignmentIndIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" fontWeight={500}>
                    {patient.assignedProviderName}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={LABEL_SX}>Territory</Typography>
                <Typography variant="body2">{patient.territory}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={LABEL_SX}>Intake Visit</Typography>
                <Typography variant="body2">
                  {format(patient.intakeVisitDate, 'MMMM d, yyyy')}
                </Typography>
              </Grid>
            </Grid>
            <Button
              variant="outlined"
              size="small"
              color="success"
              endIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => navigate('/capacity-management')}
            >
              View Assignment
            </Button>
          </>
        )}

        {/* Stage 1 — pending, no operate access */}
        {!isAssigned && !hasOperateAccess && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              This patient has been referred to the Operate team for provider intake assignment.
              A care coordinator will assign a provider and schedule the initial visit.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" fontWeight={600}>Territory:</Box> {patient.territory}
            </Typography>
          </Box>
        )}

        {/* Stage 1 — pending, has operate access — inline assignment */}
        {!isAssigned && hasOperateAccess && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Assign a provider for this patient's intake visit. Territory:{' '}
              <Box component="span" fontWeight={600}>{patient.territory}</Box>.
              Intake date:{' '}
              <Box component="span" fontWeight={600}>{format(patient.intakeVisitDate, 'MMMM d, yyyy')}</Box>.
            </Typography>

            {/* Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ ...LABEL_SX, mb: 0.75 }}>Provider Type</Typography>
                <ToggleButtonGroup
                  value={providerType}
                  exclusive
                  size="small"
                  onChange={(_, v) => v && setProviderType(v)}
                >
                  <ToggleButton value="RN" sx={{ px: 2, fontSize: '0.75rem' }}>RN</ToggleButton>
                  <ToggleButton value="PT" sx={{ px: 2, fontSize: '0.75rem' }}>PT</ToggleButton>
                  <ToggleButton value="OT" sx={{ px: 2, fontSize: '0.75rem' }}>OT</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ mt: 2.5 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AutoAwesomeIcon fontSize="small" />}
                  onClick={handleAutoAssign}
                  disabled={!snapshot || filterViableRecommendations(recommendations, false).length === 0}
                >
                  Auto-Assign
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Recommendation table */}
            {!snapshot ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : recommendations.length === 0 && filteredProviderMetadata.length === 0 ? (
              <Alert severity="warning">
                No {providerType} providers found for the {patient.territory} territory. Try a different provider type.
              </Alert>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <ProviderRecommendationRow
                  patientId={patient.id}
                  patientName={patient.patientName}
                  intakeDate={patient.intakeVisitDate}
                  recommendations={recommendations}
                  targetUtilization={TARGET_UTILIZATION}
                  onAssign={handleAssign}
                  onAssignAlternative={() => {}}
                  onAssignNextBest={() => {}}
                  onCreateProvisional={() => {}}
                />
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};
